import {
  getColumnHeader,
  normalizeComparableText,
  type RawDataColumn,
  type RawDataRow,
  type TurfRespondent,
} from './adapter';

export interface GlobalDemographicFilter {
  id: string;
  label: string;
  options?: GlobalFilterOption[];
  title?: string;
}

export interface GlobalFilterOption {
  count?: number;
  id: number | string;
  label: string;
}

export interface QuotaGroup {
  id?: number | string;
  instantSurveyGroupRef?: string;
  name?: string;
  quotaGroupRefs?: string[];
}

export type SelectedDemographicFilters = Record<string, string[]>;

export interface UsableDemographicFilter extends GlobalDemographicFilter {
  options: GlobalFilterOption[];
}

const QUOTA_GROUP_ACCESSORS = [
  'quota_group_name',
  'quota_group_ref',
  'quota_group_id',
  'quota_group',
];

export const buildUsableDemographicFilters = ({
  columns = [],
  demographicFilters = [],
}: {
  columns?: RawDataColumn[];
  demographicFilters?: GlobalDemographicFilter[];
}): UsableDemographicFilter[] => {
  const accessors = new Set(columns.map(({ accessor }) => String(accessor ?? '')));

  return demographicFilters
    .filter((filter) => accessors.has(filter.id))
    .map((filter) => ({
      ...filter,
      options: filter.options ?? [],
    }));
};

export const filterTurfRespondents = ({
  demographicFilters = [],
  quotaGroups = [],
  respondents = [],
  selectedDemographicFilters = {},
  selectedQuotaGroups,
}: {
  demographicFilters?: UsableDemographicFilter[];
  quotaGroups?: QuotaGroup[];
  respondents?: TurfRespondent[];
  selectedDemographicFilters?: SelectedDemographicFilters;
  selectedQuotaGroups?: null | string[];
}): TurfRespondent[] =>
  respondents.filter((respondent) => {
    if (!doesRespondentMatchQuotaGroups(respondent.rawRow, selectedQuotaGroups, quotaGroups)) {
      return false;
    }

    return demographicFilters.every((filter) => {
      const selectedValues = selectedDemographicFilters[filter.id] ?? [];

      if (!selectedValues.length) return true;

      return doesRowValueMatchSelectedOptions(respondent.rawRow[filter.id], selectedValues, filter);
    });
  });

export const getSelectedDemographicFilters = ({
  filters = [],
  searchParams,
}: {
  filters?: UsableDemographicFilter[];
  searchParams: URLSearchParams;
}): SelectedDemographicFilters =>
  filters.reduce<SelectedDemographicFilters>((selectedFilters, filter) => {
    const selectedValues = getSearchParamList(searchParams, filter.id);

    if (selectedValues.length) selectedFilters[filter.id] = selectedValues;

    return selectedFilters;
  }, {});

export const getSelectedQuotaGroups = (searchParams: URLSearchParams): null | string[] => {
  if (!searchParams.has('group_ids')) return null;

  return getSearchParamList(searchParams, 'group_ids');
};

export const getSearchParamList = (searchParams: URLSearchParams, key: string): string[] =>
  searchParams
    .get(key)
    ?.split(',')
    .map((value) => value.trim())
    .filter(Boolean) ?? [];

export const hasActiveTurfFilters = ({
  selectedDemographicFilters = {},
  selectedQuotaGroups,
}: {
  selectedDemographicFilters?: SelectedDemographicFilters;
  selectedQuotaGroups?: null | string[];
}): boolean =>
  selectedQuotaGroups !== null ||
  Object.values(selectedDemographicFilters).some((selectedValues) => selectedValues.length > 0);

export const toFilterGroupValue = (selectedValues: string[] = []): Record<string, boolean> =>
  selectedValues.reduce<Record<string, boolean>>((value, selectedValue) => {
    value[selectedValue] = true;
    return value;
  }, {});

const doesRespondentMatchQuotaGroups = (
  row: RawDataRow,
  selectedQuotaGroups: null | string[] | undefined,
  quotaGroups: QuotaGroup[]
): boolean => {
  if (selectedQuotaGroups == null) return true;
  if (!selectedQuotaGroups.length) return false;

  const selectedTokens = buildSelectedQuotaGroupTokens(selectedQuotaGroups, quotaGroups);
  const rowTokens = QUOTA_GROUP_ACCESSORS.map((accessor) =>
    normalizeComparableText(row[accessor])
  ).filter(Boolean);

  return rowTokens.some((rowToken) => selectedTokens.has(rowToken));
};

const buildSelectedQuotaGroupTokens = (
  selectedQuotaGroups: string[],
  quotaGroups: QuotaGroup[]
): Set<string> => {
  const selectedTokenSet = new Set(
    selectedQuotaGroups.map(normalizeComparableText).filter(Boolean)
  );

  quotaGroups.forEach((group) => {
    const groupTokens = [
      group.id,
      group.instantSurveyGroupRef,
      group.name,
      ...(group.quotaGroupRefs ?? []),
    ]
      .map(normalizeComparableText)
      .filter(Boolean);

    if (groupTokens.some((token) => selectedTokenSet.has(token))) {
      groupTokens.forEach((token) => selectedTokenSet.add(token));
    }
  });

  return selectedTokenSet;
};

const doesRowValueMatchSelectedOptions = (
  rawValue: unknown,
  selectedValues: string[],
  filter: UsableDemographicFilter
): boolean => {
  const normalizedRawValue = normalizeComparableText(rawValue);

  if (!normalizedRawValue) return false;

  const selectedTokens = buildSelectedOptionTokens(selectedValues, filter);

  return selectedTokens.has(normalizedRawValue);
};

const buildSelectedOptionTokens = (
  selectedValues: string[],
  filter: UsableDemographicFilter
): Set<string> => {
  const selectedTokenSet = new Set(selectedValues.map(normalizeComparableText).filter(Boolean));

  filter.options.forEach((option) => {
    const optionTokens = [option.id, option.label].map(normalizeComparableText).filter(Boolean);

    if (optionTokens.some((token) => selectedTokenSet.has(token))) {
      optionTokens.forEach((token) => selectedTokenSet.add(token));
    }
  });

  return selectedTokenSet;
};

export const getMissingDemographicFilterIds = ({
  columns = [],
  demographicFilters = [],
}: {
  columns?: RawDataColumn[];
  demographicFilters?: GlobalDemographicFilter[];
}): string[] => {
  const accessors = new Set(columns.map(({ accessor }) => String(accessor ?? '')));

  return demographicFilters.filter((filter) => !accessors.has(filter.id)).map(({ id }) => id);
};

export const getRawColumnLabels = (columns: RawDataColumn[] = []): string[] =>
  columns.map((column) => getColumnHeader(column)).filter(Boolean);
